import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search, BookOpen, FileDown, Pencil, RotateCcw, Save, X, Loader2,
  Compass, ClipboardList, CalendarDays, Truck, Layers, ShieldCheck, Users, Package,
  Barcode, Tags, FileText, AlertTriangle, Shield, Palette, Plug, HardDrive,
  ShieldAlert, Database, Hash, Sparkles, ChevronRight, Lightbulb, Info, AlertCircle, Flame,
} from 'lucide-react';

const ICONS = {
  BookOpen, Compass, ClipboardList, CalendarDays, Truck, Layers, ShieldCheck,
  Users, Package, Barcode, Tags, FileText, AlertTriangle, Shield, Palette,
  Plug, HardDrive, ShieldAlert, Database, Hash,
};

function SectionIcon({ name, className = "w-4 h-4" }) {
  const I = ICONS[name] || FileText;
  return <I className={className} />;
}

// ─── Helpers ──────────────────────────────────────────────────────────
const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);

// Parse h2/h3 from markdown to build a Table of Contents (skips fenced code blocks)
const extractHeadings = (markdown) => {
  if (!markdown) return [];
  const lines = markdown.split('\n');
  const headings = [];
  let inFence = false;
  for (const raw of lines) {
    if (raw.trim().startsWith('```')) { inFence = !inFence; continue; }
    if (inFence) continue;
    const m2 = raw.match(/^##\s+(.+?)\s*$/);
    const m3 = raw.match(/^###\s+(.+?)\s*$/);
    if (m2) headings.push({ level: 2, text: m2[1], id: slugify(m2[1]) });
    else if (m3) headings.push({ level: 3, text: m3[1], id: slugify(m3[1]) });
  }
  return headings;
};

// Detect "callout type" from first character/emoji of the blockquote text
const detectCallout = (text) => {
  const t = (text || '').trim();
  if (/^💡|^Tip[:\s]/i.test(t)) return { kind: 'tip', icon: Lightbulb };
  if (/^⚠️|^Warning[:\s]/i.test(t)) return { kind: 'warning', icon: AlertTriangle };
  if (/^❗|^🚨|^Danger[:\s]/i.test(t)) return { kind: 'danger', icon: Flame };
  if (/^ℹ️|^📝|^Note[:\s]/i.test(t)) return { kind: 'note', icon: Info };
  return { kind: '', icon: null };
};

// Markdown components — customise blockquote and heading rendering
const markdownComponents = {
  h2: ({ node, children, ...props }) => {
    const text = String(children?.[0] || '');
    return <h2 id={slugify(text)} {...props}>{children}</h2>;
  },
  h3: ({ node, children, ...props }) => {
    const text = String(children?.[0] || '');
    return <h3 id={slugify(text)} {...props}>{children}</h3>;
  },
  blockquote: ({ node, children, ...props }) => {
    // Pull plain text out of the first paragraph to sniff the callout type
    const firstText = (() => {
      try {
        const firstChild = node?.children?.find(c => c.type === 'element' && c.tagName === 'p');
        const text = firstChild?.children?.map(c => c.value || '').join('') || '';
        return text;
      } catch { return ''; }
    })();
    const { kind, icon: IconCmp } = detectCallout(firstText);
    if (!kind) return <blockquote {...props}>{children}</blockquote>;
    return (
      <blockquote className={`callout-${kind}`} {...props}>
        {IconCmp && (
          <div className="flex items-start gap-2.5">
            <IconCmp className="w-4 h-4 mt-1 shrink-0" />
            <div className="flex-1 min-w-0">{children}</div>
          </div>
        )}
        {!IconCmp && children}
      </blockquote>
    );
  },
};

// ─── Main component ──────────────────────────────────────────────────
export default function UserGuide() {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [categories, setCategories] = useState([]);
  const [section, setSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeHeadingId, setActiveHeadingId] = useState(null);
  const contentRef = useRef(null);

  // Flatten visible sections for nav lookup
  const flatSections = useMemo(
    () => categories.flatMap(c => c.sections.map(s => ({ ...s, category: c.category }))),
    [categories],
  );

  // TOC for current section
  const headings = useMemo(
    () => (section ? extractHeadings(section.markdown) : []),
    [section],
  );

  // Initial load
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get('/user-guide/sections');
        if (!mounted) return;
        setCategories(data.categories || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load user guide');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load section when slug changes
  useEffect(() => {
    if (!categories.length) return;
    const target = urlSlug || flatSections[0]?.slug;
    if (!target) return;
    setSectionLoading(true);
    setSearchResults(null);
    api.get(`/user-guide/sections/${target}`)
      .then(({ data }) => {
        setSection(data);
        setDraft(data.markdown);
        setEditing(false);
        setActiveHeadingId(null);
        contentRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' });
      })
      .catch((e) => {
        if (e.response?.status === 404) {
          toast.error('Section not found');
          navigate('/help', { replace: true });
        } else if (e.response?.status === 403) {
          toast.error("You don't have access to this section");
        } else {
          toast.error(e.response?.data?.detail || 'Failed to load section');
        }
      })
      .finally(() => setSectionLoading(false));
  }, [urlSlug, categories.length, flatSections, navigate]);

  // Scroll-spy: highlight current heading in the right TOC
  useEffect(() => {
    if (!section || editing) return;
    const scroller = contentRef.current;
    if (!scroller) return;
    const onScroll = () => {
      let current = null;
      for (const h of headings) {
        const el = scroller.querySelector(`#${CSS.escape(h.id)}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();
        if (rect.top - scrollerRect.top <= 80) current = h.id;
      }
      setActiveHeadingId(current);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [section, headings, editing]);

  const handleSearch = useCallback(async (q) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const { data } = await api.get('/user-guide/search', { params: { q: trimmed } });
      setSearchResults(data.results || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    const timer = setTimeout(() => handleSearch(searchQuery), 350);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const saveOverride = async () => {
    setSaving(true);
    try {
      await api.put(`/user-guide/sections/${section.slug}`, { markdown: draft });
      const { data } = await api.get(`/user-guide/sections/${section.slug}`);
      setSection(data);
      setEditing(false);
      toast.success('Override saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const revertOverride = async () => {
    if (!window.confirm('Revert this section to the shipped default? Your customisations will be lost.')) return;
    try {
      await api.delete(`/user-guide/sections/${section.slug}/override`);
      const { data } = await api.get(`/user-guide/sections/${section.slug}`);
      setSection(data);
      setDraft(data.markdown);
      setEditing(false);
      toast.success('Reverted to default');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Revert failed');
    }
  };

  const downloadPdf = async () => {
    if (!section) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/user-guide/sections/${section.slug}/pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-guide-${section.slug}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF generation failed');
    } finally {
      setDownloading(false);
    }
  };

  const scrollToHeading = (id) => {
    const scroller = contentRef.current;
    const target = scroller?.querySelector(`#${CSS.escape(id)}`);
    if (!target || !scroller) return;
    const top = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop - 24;
    scroller.scrollTo({ top, behavior: 'smooth' });
  };

  const canEdit = user?.role === 'super_admin' || user?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading user guide…
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-zinc-50 -m-4 sm:-m-6" data-testid="user-guide-page">
      {/* ── Left nav ─────────────────────────────────── */}
      <aside className="w-72 shrink-0 border-r bg-white overflow-y-auto user-guide-nav-scroll" data-testid="user-guide-nav">
        <div className="p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-zinc-900 leading-tight">User Guide</h2>
              <p className="text-[11px] text-zinc-500 leading-tight">v1.0 · Cashel Operations</p>
            </div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-zinc-400 pointer-events-none" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search the guide…"
              className="pl-8 h-9 text-sm bg-zinc-50 border-zinc-200 focus-visible:bg-white"
              data-testid="user-guide-search-input"
            />
            {searching && <Loader2 className="w-3.5 h-3.5 absolute right-2.5 top-2.5 animate-spin text-zinc-400" />}
            {searchQuery && !searching && (
              <button
                onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                className="absolute right-2 top-1.5 p-1 rounded hover:bg-zinc-200 text-zinc-400"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        <nav className="py-2">
          {searchResults !== null ? (
            <div className="px-2" data-testid="user-guide-search-results">
              <div className="px-2 py-1.5 text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                {searchResults.length} result{searchResults.length === 1 ? '' : 's'}
              </div>
              {searchResults.length === 0 && (
                <p className="px-2 py-3 text-sm text-zinc-500">No matches.</p>
              )}
              {searchResults.map((r) => (
                <Link
                  key={r.slug}
                  to={`/help/${r.slug}`}
                  onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                  className="block px-3 py-2.5 rounded-md hover:bg-indigo-50 mb-1 transition-colors group"
                  data-testid={`search-result-${r.slug}`}
                >
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-400 group-hover:text-indigo-500 mb-0.5">
                    <Sparkles className="w-3 h-3" />
                    {r.category}
                  </div>
                  <div className="text-sm font-medium text-zinc-900 mb-0.5">{r.title}</div>
                  <div className="text-xs text-zinc-500 line-clamp-2">{r.snippet}</div>
                </Link>
              ))}
            </div>
          ) : (
            categories.map((cat) => (
              <div key={cat.category} className="mb-2">
                <div className="px-4 py-1.5 text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">
                  {cat.category}
                </div>
                <ul>
                  {cat.sections.map((s) => {
                    const active = section?.slug === s.slug;
                    return (
                      <li key={s.slug}>
                        <Link
                          to={`/help/${s.slug}`}
                          className={`flex items-center gap-2.5 mx-2 px-3 py-2 text-sm rounded-md transition-all ${
                            active
                              ? 'bg-indigo-50 text-indigo-900 font-medium shadow-sm'
                              : 'text-zinc-700 hover:bg-zinc-100'
                          }`}
                          data-testid={`nav-section-${s.slug}`}
                        >
                          <SectionIcon
                            name={s.icon}
                            className={`w-4 h-4 shrink-0 ${active ? 'text-indigo-600' : 'text-zinc-400'}`}
                          />
                          <span className="truncate">{s.title}</span>
                          {active && <ChevronRight className="w-3.5 h-3.5 ml-auto text-indigo-400" />}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </nav>
      </aside>

      {/* ── Content pane ─────────────────────────────── */}
      <main ref={contentRef} className="flex-1 overflow-y-auto bg-white" data-testid="user-guide-content">
        {sectionLoading || !section ? (
          <div className="flex items-center gap-2 p-8 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="flex gap-8 max-w-6xl mx-auto px-6 lg:px-10 py-8">
            <article className="flex-1 min-w-0 max-w-3xl">
              {/* Header */}
              <div className="pb-6 mb-6 border-b border-zinc-200">
                <div className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 uppercase tracking-wider mb-2">
                  <Sparkles className="w-3.5 h-3.5" />
                  {section.category}
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 tracking-tight leading-tight">
                      {section.title}
                    </h1>
                    <div className="flex items-center gap-3 mt-3 text-xs text-zinc-500">
                      {section.source === 'override' && (
                        <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-800 gap-1" data-testid="section-source-badge">
                          <Pencil className="w-3 h-3" /> Customised
                        </Badge>
                      )}
                      {section.last_updated_at && (
                        <span className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-zinc-400" />
                          Updated{' '}
                          {new Date(section.last_updated_at).toLocaleString('en-IE', {
                            year: 'numeric', month: 'short', day: 'numeric',
                          })}
                        </span>
                      )}
                      {headings.length > 0 && (
                        <span className="flex items-center gap-1">
                          <span className="w-1 h-1 rounded-full bg-zinc-400" />
                          {headings.length} section{headings.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {canEdit && !editing && (
                      <Button variant="outline" size="sm" onClick={() => { setDraft(section.markdown); setEditing(true); }} data-testid="btn-edit-section">
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                      </Button>
                    )}
                    {canEdit && section.source === 'override' && !editing && (
                      <Button variant="outline" size="sm" onClick={revertOverride} data-testid="btn-revert-section">
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revert
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="btn-download-pdf">
                      {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileDown className="w-3.5 h-3.5 mr-1" />}
                      PDF
                    </Button>
                  </div>
                </div>
              </div>

              {/* Body */}
              {editing ? (
                <Card className="border-amber-200 bg-amber-50/30">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-100/60 rounded-md px-3 py-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                      <p>
                        You're editing a <strong>tenant-specific override</strong>. The shipped default markdown is untouched.
                        Click <strong>Revert</strong> on the header any time to discard customisations.
                      </p>
                    </div>
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      className="w-full h-[60vh] font-mono text-sm border rounded-md p-3 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      data-testid="section-edit-textarea"
                    />
                    <div className="flex items-center gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => { setEditing(false); setDraft(section.markdown); }}>
                        <X className="w-4 h-4 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={saveOverride} disabled={saving} data-testid="btn-save-section">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                        Save Override
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="prose-guide" data-testid="section-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {section.markdown}
                  </ReactMarkdown>
                </div>
              )}
            </article>

            {/* Right TOC — only when there are 2+ h2/h3 and not editing */}
            {!editing && headings.length >= 2 && (
              <aside className="hidden xl:block w-56 shrink-0">
                <div className="sticky top-8">
                  <div className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-3 px-3">
                    On this page
                  </div>
                  <nav className="space-y-0.5" data-testid="user-guide-toc">
                    {headings.map((h) => {
                      const active = activeHeadingId === h.id;
                      return (
                        <button
                          key={h.id}
                          type="button"
                          onClick={() => scrollToHeading(h.id)}
                          className={`block w-full text-left text-xs px-3 py-1.5 rounded transition-colors ${
                            h.level === 3 ? 'pl-7' : ''
                          } ${
                            active
                              ? 'text-indigo-700 font-medium bg-indigo-50 border-l-2 border-indigo-600 pl-[10px]'
                              : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 border-l-2 border-transparent'
                          } ${h.level === 3 && active ? 'pl-[26px]' : ''}`}
                        >
                          {h.text}
                        </button>
                      );
                    })}
                  </nav>
                </div>
              </aside>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
